import { slicedToArray as _slicedToArray } from '../_virtual/_rollupPluginBabelHelpers.js';
import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { throttle } from 'lodash-es';
import { useStickyHeight } from '@bucketplace/ui';

var getSelectorId = function getSelectorId(id) {
  return isNaN(Number(id)) ? "#".concat(id) : "#\\".concat(id);
};
var useActiveTab = function useActiveTab(_ref) {
  var targetAnchorIds = _ref.targetAnchorIds,
    scrollRef = _ref.scrollRef,
    containerRef = _ref.containerRef;
  var _useState = useState(targetAnchorIds[0]),
    _useState2 = _slicedToArray(_useState, 2),
    activeTab = _useState2[0],
    setActiveTab = _useState2[1];
  var outerStickyData = useStickyHeight(containerRef);
  var beforeScroll = useRef(0);

  /**
   * 각 ancorId 별로 탭이 활성화 되는 시작 위치를 찾는 함수
   */
  var getScrollData = useCallback(function () {
    var _document$querySelect, _document$querySelect2, _targetAnchorIds$;
    if (targetAnchorIds.length === 0) {
      return {};
    }
    var stickyHeight = (_document$querySelect = (_document$querySelect2 = document.querySelector(getSelectorId((_targetAnchorIds$ = targetAnchorIds[0]) !== null && _targetAnchorIds$ !== void 0 ? _targetAnchorIds$ : ''))) === null || _document$querySelect2 === void 0 || (_document$querySelect2 = _document$querySelect2.parentElement) === null || _document$querySelect2 === void 0 ? void 0 : _document$querySelect2.offsetHeight) !== null && _document$querySelect !== void 0 ? _document$querySelect : 0;
    var scrollData = {};
    var tabItemElements = Array.from(document.querySelectorAll(targetAnchorIds.map(function (id) {
      return getSelectorId(id);
    }).join(', ')));
    for (var _i = 0, _tabItemElements = tabItemElements; _i < _tabItemElements.length; _i++) {
      var tabItemElement = _tabItemElements[_i];
      var absoluteTop = Math.round(window.pageYOffset + tabItemElement.getBoundingClientRect().top);
      var elementTop = scrollRef.current ? tabItemElement.offsetTop : absoluteTop;
      scrollData[tabItemElement.id] = elementTop - stickyHeight - outerStickyData.current;
    }
    return scrollData;
  }, [outerStickyData, scrollRef, targetAnchorIds]);

  /**
   * 스크롤 이벤트가 발생할 때마다 호출.
   * 현재 스크롤의 위치와 getScrollData를 통해 받은 시작 위치가 동일한 경우 탭을 활성화 시킴
   */
  var throttledScroll = useMemo(function () {
    return throttle(function () {
      var _scrollRef$current$sc, _scrollRef$current, _scrollTargets$slice$;
      var scrollY = Math.round((_scrollRef$current$sc = (_scrollRef$current = scrollRef.current) === null || _scrollRef$current === void 0 ? void 0 : _scrollRef$current.scrollTop) !== null && _scrollRef$current$sc !== void 0 ? _scrollRef$current$sc : window.scrollY);
      if (beforeScroll.current === scrollY) {
        return;
      }
      var scrollData = getScrollData();
      var scrollTargets = targetAnchorIds.reduce(function (acc, id) {
        var scrollPosition = scrollData[id];
        if (scrollPosition == null) {
          return acc;
        }
        return acc.concat([[scrollPosition, id]]);
      }, []);
      var current = outerStickyData.current,
        min = outerStickyData.min,
        max = outerStickyData.max;
      var direction = beforeScroll.current < scrollY ? 'down' : 'up';
      var hideOuterSticky = direction === 'up' && current !== max;
      var scroll = hideOuterSticky ? scrollY + (max - min) : scrollY;
      var target = (_scrollTargets$slice$ = scrollTargets.slice().reverse().find(function (e) {
        return e[0] <= scroll;
      })) !== null && _scrollTargets$slice$ !== void 0 ? _scrollTargets$slice$ : scrollTargets[0];
      beforeScroll.current = scrollY;
      setActiveTab(target === null || target === void 0 ? void 0 : target[1]);
    }, 300);
  }, [getScrollData, outerStickyData, scrollRef, targetAnchorIds]);

  /**
   * onChangeTab 이벤트를 호출했을 때 실행되며, 주로 탭을 클릭했을 때 실행됨.
   * getScrollData를 통해 선택한 탭의 시작 위치로 스크롤 이동.
   * 스크롤 이벤트가 발생하기 때문에 throttledScroll이 호출되고 활성화된 탭이 변경
   * @param tabId 선택한 탭의 id
   */
  var handleChangeTab = useCallback(function (tabId) {
    var _scrollRef$current$sc2, _scrollRef$current2, _scrollRef$current3;
    var scrollY = Math.round((_scrollRef$current$sc2 = (_scrollRef$current2 = scrollRef.current) === null || _scrollRef$current2 === void 0 ? void 0 : _scrollRef$current2.scrollTop) !== null && _scrollRef$current$sc2 !== void 0 ? _scrollRef$current$sc2 : window.scrollY);
    var scrollElement = (_scrollRef$current3 = scrollRef.current) !== null && _scrollRef$current3 !== void 0 ? _scrollRef$current3 : window;
    var scrollData = getScrollData();
    var scrollPosition = scrollData[tabId];
    if (scrollPosition == null) {
      return;
    }
    var current = outerStickyData.current,
      min = outerStickyData.min,
      max = outerStickyData.max;
    var direction = scrollY - scrollPosition - current > 0 ? 'up' : 'down';
    var diff = 0;
    if (direction === 'up' && current !== max) {
      diff = -(max - min);
    } else if (direction === 'down' && current === max) {
      diff = max - min;
    }
    scrollElement === null || scrollElement === void 0 || scrollElement.scrollTo(0, scrollPosition + diff);
  }, [getScrollData, outerStickyData, scrollRef]);
  useEffect(function () {
    var _scrollRef$current4;
    var scrollElement = (_scrollRef$current4 = scrollRef.current) !== null && _scrollRef$current4 !== void 0 ? _scrollRef$current4 : window;
    scrollElement.addEventListener('scroll', throttledScroll, {
      passive: true
    });
    return function () {
      scrollElement.removeEventListener('scroll', throttledScroll);
    };
  }, [scrollRef, throttledScroll]);
  return {
    activeTab: activeTab,
    onChangeTab: handleChangeTab
  };
};

export { useActiveTab };
//# sourceMappingURL=useActiveTab.js.map
